import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Search, AlertTriangle, Clock, Loader2, RefreshCw, Save, ChevronRight } from "lucide-react";
import { Help, TermLabel, Explained, CodeHelp } from "./shared/Help";
import type { GlossaryKey } from "./shared/acaGlossary";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import {
  useAcaYear, useAcaEmployeeDetail, useMonthlyHours, useW2Wages, useRecompute, useSaveMonthlyHours, useSaveW2Wages,
} from "./shared/dashboardEmployeesHooks";
import {
  ACA_STATUS_LABELS, AFFORDABILITY_LABELS, MONTH_LABELS, SAFE_HARBOR_LABELS, FIX_AREA_LABELS,
  fmtDate, money, taxYearOptions,
  type EmployeeYearResult, type MonthResult, type AcaException,
} from "./shared/dashboardEmployeesTypes";

interface ACAEmployeesProps {
  clientId?: number;
}

type Filter = "all" | "full_time" | "not_full_time" | "measuring_lnap" | "form_required" | "has_exceptions";

const FILTER_LABELS: Record<Filter, string> = {
  all: "All employees",
  full_time: "Full-time (130+ hrs in at least one month)",
  not_full_time: "Part-time (never 130+ hrs)",
  measuring_lnap: "Measuring / non-assessment period",
  form_required: "1095-C required",
  has_exceptions: "Has exceptions",
};

const exceptionsOf = (e: EmployeeYearResult): AcaException[] => [...e.exceptions, ...e.months.flatMap((m) => m.exceptions)];

function affordabilityBadge(status: string) {
  const cls: Record<string, string> = {
    affordable: "bg-green-600",
    not_affordable: "bg-red-600",
    mixed: "bg-red-600",
    pending_data: "bg-amber-500",
    not_applicable: "bg-gray-400",
  };
  const term: GlossaryKey = status === "pending_data" ? "pendingData" : status === "not_applicable" ? "notApplicable" : status === "affordable" ? "affordability" : "line16Blank";
  return <Explained term={term}><Badge className={cls[status] || "bg-gray-400"}>{AFFORDABILITY_LABELS[status] || status}</Badge></Explained>;
}

const CLASSIFICATION_TERM: Record<string, GlossaryKey> = {
  "Full-time": "fullTime",
  "Part-time": "partTime",
  "Measuring": "measuring",
  "Hours missing": "hoursMissing",
  "Non-assessment period": "lnap",
  "Not employed": "population",
};

const LNAP_TERM: Record<string, GlossaryKey> = {
  first_partial_month: "firstPartialMonth",
  waiting_period: "waitingPeriodLnap",
  initial_measurement_period: "initialMeasurementPeriod",
  first_ale_year: "firstAleYear",
};

/** Hours-based ACA classification for the year: the HR label never enters into it. */
function classifyYear(e: EmployeeYearResult): { label: string; cls: string; detail: string } {
  const s = e.summary;
  const employed = e.months.filter((m) => m.employed);
  const measuring = employed.filter((m) => m.acaStatus === "measuring").length;
  const unknown = employed.filter((m) => m.acaStatus === "unknown").length;
  const lnapNote = s.lnapMonths > 0 ? ` · ${s.lnapMonths} in a non-assessment period` : "";
  if (s.fullTimeMonths > 0) {
    return { label: "Full-time", cls: "bg-green-600", detail: `${s.fullTimeMonths} of ${s.employedMonths} employed months at 130+ hrs${lnapNote}` };
  }
  if (employed.length === 0) return { label: "Not employed", cls: "bg-gray-400", detail: "no employment in this year" };
  if (employed.every((m) => m.lnapReason)) {
    return { label: "Non-assessment period", cls: "bg-blue-500", detail: `all ${employed.length} employed month${employed.length === 1 ? "" : "s"} fall in a non-assessment period (partial first month, waiting period or initial measurement); no assessable month yet` };
  }
  if (unknown > 0 && unknown + measuring === employed.length) {
    return { label: "Hours missing", cls: "bg-red-600", detail: "no recorded hours and no scheduled hours" };
  }
  if (measuring > 0) return { label: "Measuring", cls: "bg-amber-500", detail: `initial measurement period · ${measuring} months${lnapNote}` };
  return { label: "Part-time", cls: "bg-gray-500", detail: `under 130 hrs in every employed month (${s.employedMonths})${lnapNote}` };
}

function statusBadge(m: MonthResult) {
  if (!m.employed) return <Explained term="population"><Badge variant="outline" className="text-[10px]">Not employed</Badge></Explained>;
  if (m.lnapReason) return <Explained term={LNAP_TERM[m.lnapReason] ?? "lnap"}><Badge className="bg-blue-500 text-[10px]">Non-assessment</Badge></Explained>;
  switch (m.acaStatus) {
    case "full_time": return <Explained term="fullTime"><Badge className="bg-green-600 text-[10px]">Full-time</Badge></Explained>;
    case "non_full_time": return <Explained term="partTime"><Badge variant="secondary" className="text-[10px]">Part-time</Badge></Explained>;
    case "measuring": return <Explained term="measuring"><Badge className="bg-amber-500 text-[10px]">Measuring</Badge></Explained>;
    default: return <Explained term="hoursMissing"><Badge variant="destructive" className="text-[10px]">Unknown</Badge></Explained>;
  }
}

function hoursCell(m: MonthResult) {
  if (m.hours == null) return <span className="text-muted-foreground">—</span>;
  const est = m.hoursSource === "schedule_estimate";
  return (
    <span className="inline-flex items-center gap-1">
      {m.hours.toFixed(1)}
      {est && <Explained term="hoursEstimated"><Badge variant="outline" className="text-[9px] px-1 py-0">est.</Badge></Explained>}
      {m.hoursSource === "measurement_average" && <Explained term="measurementAverage"><Badge variant="outline" className="text-[9px] px-1 py-0">avg</Badge></Explained>}
    </span>
  );
}

export default function ACAEmployees({ clientId: propClientId }: ACAEmployeesProps) {
  const { user } = useAuth();
  const clientId = propClientId ?? (user as any)?.clientId;
  const [taxYear, setTaxYear] = useState(new Date().getFullYear() - 1);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showHoursGrid, setShowHoursGrid] = useState(false);
  const { toast } = useToast();

  const { data: yearData, isLoading, error } = useAcaYear(clientId, taxYear);
  const recompute = useRecompute(clientId, taxYear);

  const employees = useMemo(() => {
    const all = yearData?.result?.employees ?? [];
    const sorted = sortEmployeesByFullName(all as any) as EmployeeYearResult[];
    const q = search.trim().toLowerCase();
    return sorted.filter((e) => {
      if (q && !`${e.firstName} ${e.lastName}`.toLowerCase().includes(q)) return false;
      switch (filter) {
        case "full_time": return e.summary.fullTimeMonths > 0;
        case "not_full_time": return e.summary.fullTimeMonths === 0;
        case "measuring_lnap": return e.summary.lnapMonths > 0 || e.months.some((m) => m.acaStatus === "measuring");
        case "form_required": return !!e.formRequired;
        case "has_exceptions": return exceptionsOf(e).length > 0;
        default: return true;
      }
    });
  }, [yearData, search, filter]);

  const counts = useMemo(() => {
    const all = yearData?.result?.employees ?? [];
    return {
      population: all.length,
      fullTime: all.filter((e) => e.summary.fullTimeMonths > 0).length,
      measuring: all.filter((e) => e.summary.lnapMonths > 0 || e.months.some((m) => m.acaStatus === "measuring")).length,
      forms: all.filter((e) => e.formRequired).length,
      exceptions: all.filter((e) => exceptionsOf(e).length > 0).length,
    };
  }, [yearData]);

  const selected = employees.find((e) => e.employeeId === selectedId) ?? yearData?.result?.employees.find((e) => e.employeeId === selectedId) ?? null;

  if (!clientId) return <Alert><AlertDescription>No employer selected.</AlertDescription></Alert>;

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <TermLabel term="taxYear"><h2 className="text-xl font-semibold">ACA Employees · {taxYear}</h2></TermLabel>
            <p className="text-sm text-muted-foreground">
              <TermLabel term="population">Population built from employment history overlapping {taxYear}.</TermLabel>{" "}
              <TermLabel term="hoursOfService">Status is determined month by month from hours of service.</TermLabel>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(taxYear)} onValueChange={(v) => { setTaxYear(Number(v)); setSelectedId(null); }}>
              <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
              <SelectContent>{taxYearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Explained term="hoursOfService">
              <Button variant="outline" onClick={() => setShowHoursGrid(true)} disabled={!yearData}>
                <Clock className="h-4 w-4 mr-2" />Enter monthly hours
              </Button>
            </Explained>
            <Explained term="recalculate">
              <Button variant="outline" onClick={() => recompute.mutate(undefined, { onError: (e: any) => toast({ title: "Recalculation failed", description: e.message, variant: "destructive" }) })} disabled={recompute.isPending}>
                <RefreshCw className={`h-4 w-4 mr-2 ${recompute.isPending ? "animate-spin" : ""}`} />Recalculate
              </Button>
            </Explained>
          </div>
        </div>

        {error && (
          <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>{(error as any).message}</AlertDescription></Alert>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {([
            ["Population", counts.population, "all", ["population"]],
            ["Full-time (any month)", counts.fullTime, "full_time", ["fullTime"]],
            ["Measuring / non-assessment", counts.measuring, "measuring_lnap", ["measuring", "lnap"]],
            ["1095-C required", counts.forms, "form_required", ["formRequired"]],
            ["With exceptions", counts.exceptions, "has_exceptions", ["exceptionWarning"]],
          ] as Array<[string, number, Filter, GlossaryKey[]]>).map(([label, value, f, terms]) => (
            <Card key={f} className={`cursor-pointer ${filter === f ? "border-primary" : ""}`} onClick={() => setFilter(f)}>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{value}</div>
                <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <span>{label}</span>
                  {terms.map((t) => <Help key={t} term={t} />)}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-base">Employees ({employees.length})</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search name" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 w-[220px]" />
                </div>
                <Select value={filter} onValueChange={(v) => setFilter(v as Filter)}>
                  <SelectTrigger className="w-[240px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(FILTER_LABELS) as Filter[]).map((f) => <SelectItem key={f} value={f}>{FILTER_LABELS[f]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">
              Full-time means 130 or more hours of service in a calendar month (the ACA equivalent of 30 hours a week), determined month by month
              from recorded hours, or estimated from scheduled hours when none are recorded. A non-assessment period is a month the IRS does not
              assess (first partial month, waiting period, or initial measurement period). The employer's own job label is not used.
            </p>
            {isLoading ? (
              <div className="py-12 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
            ) : employees.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground text-sm">No employees match.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Hired</TableHead>
                    <TableHead>Terminated</TableHead>
                    <TableHead><TermLabel term="fullTime">ACA classification ({taxYear})</TermLabel></TableHead>
                    <TableHead><TermLabel term="formRequired">1095-C</TermLabel></TableHead>
                    <TableHead><TermLabel term="affordability">Affordability</TermLabel></TableHead>
                    <TableHead className="text-right"><TermLabel term="exceptionsColumn">Exceptions</TermLabel></TableHead>
                    <TableHead className="w-[30px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((e) => {
                    const ex = exceptionsOf(e);
                    const blocking = ex.filter((x) => x.severity === "blocking").length;
                    const cls = classifyYear(e);
                    return (
                      <TableRow key={e.employeeId} className="cursor-pointer" onClick={() => setSelectedId(e.employeeId)}>
                        <TableCell className="font-medium">{e.firstName} {e.lastName}</TableCell>
                        <TableCell className="text-sm">{fmtDate(e.hireDate)}</TableCell>
                        <TableCell className="text-sm">{fmtDate(e.terminationDate)}</TableCell>
                        <TableCell className="text-xs">
                          <Explained term={CLASSIFICATION_TERM[cls.label] ?? "fullTime"}><Badge className={cls.cls}>{cls.label}</Badge></Explained>
                          <div className="text-muted-foreground mt-1">{cls.detail}</div>
                        </TableCell>
                        <TableCell className="text-xs">
                          {e.formRequired === "full_time" && <Explained term="formRequired"><Badge variant="secondary">Required · full-time</Badge></Explained>}
                          {e.formRequired === "self_insured_enrolled" && <Explained term="code1G"><Badge variant="secondary">Required · self-insured (1G)</Badge></Explained>}
                          {!e.formRequired && <Explained term="formRequired"><span className="text-muted-foreground">Not required</span></Explained>}
                        </TableCell>
                        <TableCell>{affordabilityBadge(e.summary.status)}</TableCell>
                        <TableCell className="text-right">
                          {ex.length === 0 ? <span className="text-muted-foreground">—</span> : (
                            <span className="inline-flex gap-1">
                              {blocking > 0 && <Explained term="exceptionBlocking"><Badge variant="destructive">{blocking}</Badge></Explained>}
                              {ex.length - blocking > 0 && <Explained term="exceptionWarning"><Badge variant="secondary">{ex.length - blocking}</Badge></Explained>}
                            </span>
                          )}
                        </TableCell>
                        <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <EmployeeTimelineSheet
          clientId={clientId}
          taxYear={taxYear}
          employee={selected}
          onClose={() => setSelectedId(null)}
        />

        {showHoursGrid && yearData && (
          <HoursGridDialog
            clientId={clientId}
            taxYear={taxYear}
            employees={yearData.result.employees}
            onClose={() => setShowHoursGrid(false)}
          />
        )}
      </div>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// 12-month timeline drawer
// ---------------------------------------------------------------------------
function EmployeeTimelineSheet({ clientId, taxYear, employee, onClose }: { clientId: number; taxYear: number; employee: EmployeeYearResult | null; onClose: () => void }) {
  const { toast } = useToast();
  const { data: detail, isLoading: detailLoading } = useAcaEmployeeDetail(clientId, taxYear, employee?.employeeId ?? null);
  const { data: hoursRows } = useMonthlyHours(clientId, taxYear);
  const { data: w2Rows } = useW2Wages(clientId, taxYear);
  const saveHours = useSaveMonthlyHours(clientId, taxYear);
  const saveW2 = useSaveW2Wages(clientId, taxYear);

  const [hours, setHours] = useState<string[]>(Array(12).fill(""));
  const [w2, setW2] = useState<string>("");
  const [w2Final, setW2Final] = useState(false);

  useEffect(() => {
    if (!employee) return;
    const next = Array(12).fill("");
    for (const r of hoursRows ?? []) if (r.employeeId === employee.employeeId) next[r.month - 1] = String(r.hours);
    setHours(next);
    const w = (w2Rows ?? []).find((r) => r.employeeId === employee.employeeId);
    setW2(w?.box1Wages == null ? "" : String(w.box1Wages));
    setW2Final(!!w?.isFinal);
  }, [employee?.employeeId, hoursRows, w2Rows]);

  if (!employee) return <Sheet open={false} />;
  const full = detail?.employee ?? employee;
  const yearExceptions = full.exceptions;

  const submitHours = () => {
    const rows = hours.map((h, i) => ({ employeeId: employee.employeeId, month: i + 1, hours: h.trim() === "" ? null : Number(h) }));
    if (rows.some((r) => r.hours != null && (!Number.isFinite(r.hours) || r.hours < 0))) {
      toast({ title: "Invalid hours", description: "Hours must be non-negative numbers.", variant: "destructive" });
      return;
    }
    saveHours.mutate(rows, {
      onSuccess: () => toast({ title: "Hours saved", description: "Monthly hours recorded and the year recalculated." }),
      onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
    });
  };
  const submitW2 = () => {
    const wages = w2.trim() === "" ? null : Number(w2.replace(/[^0-9.\-]/g, ""));
    if (wages != null && (!Number.isFinite(wages) || wages < 0)) {
      toast({ title: "Invalid wages", description: "Enter a non-negative amount.", variant: "destructive" });
      return;
    }
    saveW2.mutate([{ employeeId: employee.employeeId, box1Wages: wages, isFinal: w2Final }], {
      onSuccess: () => toast({ title: "W-2 wages saved", description: "The year was recalculated." }),
      onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
    });
  };

  return (
    <Sheet open={!!employee} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-5xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{full.firstName} {full.lastName} · {taxYear}</SheetTitle>
          <SheetDescription>
            Hired {fmtDate(full.hireDate)}{full.terminationDate ? ` · terminated ${fmtDate(full.terminationDate)}` : ""}
            {full.periods.length > 1 && <> · <TermLabel term="employmentPeriods">{full.periods.length} employment periods</TermLabel></>}
            {" · "}<TermLabel term="planStartMonth">Plan start month {full.planStartMonth}</TermLabel>
            {" · "}<TermLabel term={full.formRequired === "self_insured_enrolled" ? "code1G" : "formRequired"}>{full.formRequired ? `1095-C required (${full.formRequired === "full_time" ? "ACA full-time" : "self-insured enrollee, 1G"})` : "1095-C not required"}</TermLabel>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-5">
          {(() => { const c = classifyYear(full); const s = full.summary; const partTime = full.months.filter((m) => m.employed && m.acaStatus === "non_full_time").length; return (
            <div className="rounded-md border p-3 text-sm space-y-1">
              <div className="flex items-center gap-2"><Explained term={CLASSIFICATION_TERM[c.label] ?? "fullTime"}><Badge className={c.cls}>{c.label}</Badge></Explained><span className="text-muted-foreground text-xs">{c.detail}</span>{affordabilityBadge(s.status)}</div>
              <div className="text-xs text-muted-foreground">
                <TermLabel term="population">Employed {s.employedMonths} of 12 months</TermLabel>
                {" · "}<TermLabel term="fullTime">full-time in {s.fullTimeMonths}</TermLabel>
                {" · "}<TermLabel term="partTime">part-time in {partTime}</TermLabel>
                {s.lnapMonths > 0 && <> · <TermLabel term="lnap">{s.lnapMonths} in a non-assessment period</TermLabel></>}
                {" · "}<TermLabel term="offer">coverage offered for the full month in {s.offeredMonths}</TermLabel>
                {" · "}<TermLabel term="enrolledMonth">enrolled in {s.enrolledMonths}</TermLabel>
              </div>
            </div>
          ); })()}

          {yearExceptions.length > 0 && (
            <Alert variant={yearExceptions.some((x) => x.severity === "blocking") ? "destructive" : "default"}>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  {yearExceptions.map((x, i) => (
                    <li key={i}><span className="font-medium uppercase">{x.severity}</span> — {x.message}{x.fix ? ` (fix: ${FIX_AREA_LABELS[x.fix.area] || x.fix.area})` : ""}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Monthly timeline */}
          <div className="overflow-x-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs w-[150px]">Month</TableHead>
                  {MONTH_LABELS.map((m) => <TableHead key={m} className="text-xs text-center px-1 min-w-[60px]">{m}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="text-xs font-medium"><TermLabel term="fullTime">ACA status</TermLabel></TableCell>
                  {full.months.map((m) => <TableCell key={m.month} className="text-center px-1">{statusBadge(m)}</TableCell>)}
                </TableRow>
                <TableRow>
                  <TableCell className="text-xs font-medium"><TermLabel term="hoursOfService">Hours of service</TermLabel></TableCell>
                  {full.months.map((m) => <TableCell key={m.month} className="text-xs text-center px-1">{hoursCell(m)}</TableCell>)}
                </TableRow>
                <TableRow>
                  <TableCell className="text-xs font-medium"><TermLabel term="lnap">Non-assessment</TermLabel></TableCell>
                  {full.months.map((m) => (
                    <TableCell key={m.month} className="text-xs text-center px-1">
                      {m.lnapReason ? (
                        <Explained term={LNAP_TERM[m.lnapReason] ?? "lnap"} title>
                          <Badge className="bg-blue-500 text-[9px]">2D</Badge>
                        </Explained>
                      ) : "—"}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell className="text-xs font-medium"><TermLabel term="offer">Offer</TermLabel></TableCell>
                  {full.months.map((m) => (
                    <TableCell key={m.month} className="text-xs text-center px-1">
                      {m.offered ? (
                        <Explained text={`${m.offerPlanName || "Plan"}${m.offerMinimumValue === false ? " (not minimum value)" : ""}${m.offerSpouse ? " · spouse" : ""}${m.offerDependents ? " · dependents" : ""}. ${"The lowest-cost minimum-value plan this employee was eligible for every day of the month."}`}>
                          <span className="text-green-700">Yes</span>
                        </Explained>
                      ) : m.offered === false ? <Explained term="offer"><span className="text-muted-foreground">No</span></Explained> : "—"}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell className="text-xs font-medium"><TermLabel term="line14">Line 14</TermLabel></TableCell>
                  {full.months.map((m) => (
                    <TableCell key={m.month} className="text-xs text-center px-1 font-mono">
                      {m.line14 ? (
                        <CodeHelp code={m.line14} line={14} />
                      ) : <Explained term="exceptionBlocking"><Badge variant="destructive" className="text-[9px]">unresolved</Badge></Explained>}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell className="text-xs font-medium"><TermLabel term="line15">Line 15 required contribution</TermLabel></TableCell>
                  {full.months.map((m) => (
                    <TableCell key={m.month} className="text-xs text-center px-1">
                      {m.line15 == null ? "—" : (
                        <Explained term={m.line15Source === "manual_override" ? "override" : "employeeRequiredContribution"}>
                          <span>{money(m.line15)}{m.line15Source === "manual_override" ? "*" : ""}</span>
                        </Explained>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell className="text-xs font-medium"><TermLabel term="line16">Line 16</TermLabel></TableCell>
                  {full.months.map((m) => (
                    <TableCell key={m.month} className="text-xs text-center px-1 font-mono">
                      {m.line16 ? <CodeHelp code={m.line16} line={16} /> : (m.employed ? <CodeHelp code={null} line={16} className="text-muted-foreground" /> : <span className="text-muted-foreground">blank</span>)}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell className="text-xs font-medium"><TermLabel term="enrolledMonth">Enrolled</TermLabel></TableCell>
                  {full.months.map((m) => <TableCell key={m.month} className="text-xs text-center px-1">{m.enrolled ? <Explained term="enrolledMonth"><span>Yes</span></Explained> : m.employed ? "No" : "—"}</TableCell>)}
                </TableRow>
                <TableRow>
                  <TableCell className="text-xs font-medium"><TermLabel term="affordability">Affordability</TermLabel></TableCell>
                  {full.months.map((m) => (
                    <TableCell key={m.month} className="text-xs text-center px-1">
                      {m.affordability === "not_applicable" ? <Explained term="notApplicable"><span>—</span></Explained> : (
                        <Explained
                          term={m.affordability === "pending_data" ? "pendingData" : m.affordability === "affordable" ? "affordability" : "line16Blank"}
                          text={`${m.safeHarbor ? SAFE_HARBOR_LABELS[m.safeHarbor] : "No safe harbor"} · threshold ${money(m.threshold)}${m.affordabilityPct != null ? ` at ${m.affordabilityPct}%` : ""}. ${m.affordability === "affordable" ? "The required contribution is at or below the safe-harbor threshold." : m.affordability === "not_affordable" ? "The required contribution is above the safe-harbor threshold; no Line 16 relief code applies." : "An input is missing (wages not final, no compensation, or the contribution could not be priced), so no determination yet."}`}
                        >
                          <Badge className={`text-[9px] ${m.affordability === "affordable" ? "bg-green-600" : m.affordability === "not_affordable" ? "bg-red-600" : "bg-amber-500"}`}>
                            {m.affordability === "affordable" ? "OK" : m.affordability === "not_affordable" ? "No" : "Pending"}
                          </Badge>
                        </Explained>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* Editable source data */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm"><TermLabel term="hoursOfService">Hours of service · {taxYear}</TermLabel></CardTitle>
                <CardDescription><TermLabel term="hoursEstimated">Actual hours per month (including paid leave). Blank months are estimated from scheduled weekly hours.</TermLabel></CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-6 gap-2">
                  {MONTH_LABELS.map((label, i) => (
                    <div key={label}>
                      <Label className="text-[10px] text-muted-foreground">{label}</Label>
                      <Input value={hours[i]} onChange={(e) => setHours((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))} placeholder="—" className="h-8 text-xs" inputMode="decimal" />
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-3">
                  <Button size="sm" onClick={submitHours} disabled={saveHours.isPending}>
                    {saveHours.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save hours
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm"><TermLabel term="w2Wages">W-2 Box 1 wages · {taxYear}</TermLabel></CardTitle>
                <CardDescription><TermLabel term="w2SafeHarbor">Used only by the W-2 safe harbor. Unfinalized wages show as Pending Data.</TermLabel></CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input value={w2} onChange={(e) => setW2(e.target.value)} placeholder="0.00" inputMode="decimal" />
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={w2Final} onCheckedChange={(v) => setW2Final(v === true)} />
                  Final (year-end)
                </label>
                <div className="flex justify-end">
                  <Button size="sm" onClick={submitW2} disabled={saveW2.isPending}>
                    {saveW2.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save W-2
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Show your work */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm"><TermLabel term="showYourWork">Show your work</TermLabel></CardTitle>
              <CardDescription>How each month's codes were determined.</CardDescription>
            </CardHeader>
            <CardContent>
              {detailLoading ? (
                <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
              ) : (
                <div className="space-y-3">
                  {(detail?.employee.months ?? full.months).map((m) => (
                    <div key={m.month} className="text-xs">
                      <div className="font-medium">{MONTH_LABELS[m.month - 1]} · {ACA_STATUS_LABELS[m.acaStatus]} · 14: {m.line14 ?? "—"} · 15: {m.line15 == null ? "—" : money(m.line15)} · 16: {m.line16 ?? "blank"}</div>
                      <ul className="list-disc list-inside text-muted-foreground">
                        {(m.explanation ?? []).map((line, i) => <li key={i}>{line}</li>)}
                        {m.exceptions.map((x, i) => <li key={`x${i}`} className={x.severity === "blocking" ? "text-red-700" : "text-amber-700"}>{x.severity}: {x.message}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Bulk monthly hours grid
// ---------------------------------------------------------------------------
function HoursGridDialog({ clientId, taxYear, employees, onClose }: { clientId: number; taxYear: number; employees: EmployeeYearResult[]; onClose: () => void }) {
  const { toast } = useToast();
  const { data: hoursRows, isLoading } = useMonthlyHours(clientId, taxYear);
  const save = useSaveMonthlyHours(clientId, taxYear);
  const sorted = useMemo(() => sortEmployeesByFullName(employees as any) as EmployeeYearResult[], [employees]);
  const [grid, setGrid] = useState<Record<number, string[]>>({});
  const [dirty, setDirty] = useState<Set<string>>(new Set());

  useEffect(() => {
    const next: Record<number, string[]> = {};
    for (const e of sorted) next[e.employeeId] = Array(12).fill("");
    for (const r of hoursRows ?? []) if (next[r.employeeId]) next[r.employeeId][r.month - 1] = String(r.hours);
    setGrid(next);
    setDirty(new Set());
  }, [hoursRows, sorted]);

  const update = (employeeId: number, i: number, v: string) => {
    setGrid((prev) => ({ ...prev, [employeeId]: prev[employeeId].map((x, j) => (j === i ? v : x)) }));
    setDirty((prev) => new Set(prev).add(`${employeeId}-${i}`));
  };

  const submit = () => {
    const rows: Array<{ employeeId: number; month: number; hours: number | null }> = [];
    for (const key of Array.from(dirty)) {
      const [emp, idx] = key.split("-").map(Number);
      const raw = grid[emp]?.[idx] ?? "";
      const hours = raw.trim() === "" ? null : Number(raw);
      if (hours != null && (!Number.isFinite(hours) || hours < 0)) {
        toast({ title: "Invalid hours", description: "Hours must be non-negative numbers.", variant: "destructive" });
        return;
      }
      rows.push({ employeeId: emp, month: idx + 1, hours });
    }
    if (rows.length === 0) { onClose(); return; }
    save.mutate(rows, {
      onSuccess: (out: any) => { toast({ title: "Hours saved", description: `${out?.count ?? rows.length} month(s) updated; year recalculated.` }); onClose(); },
      onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
    });
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle><TermLabel term="hoursOfService">Monthly hours of service · {taxYear}</TermLabel></DialogTitle>
          <DialogDescription>
            Hours per employee per month for the {taxYear} population.{" "}
            <TermLabel term="hoursEstimated">Blank = estimated from scheduled weekly hours (flagged as an estimate).</TermLabel>{" "}
            Employees not employed in a month can be left blank.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto border rounded-md">
          {isLoading ? (
            <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sticky left-0 bg-background min-w-[180px]">Employee</TableHead>
                  {MONTH_LABELS.map((m) => <TableHead key={m} className="text-xs text-center px-1 min-w-[64px]">{m}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((e) => (
                  <TableRow key={e.employeeId}>
                    <TableCell className="text-xs sticky left-0 bg-background font-medium">
                      {e.firstName} {e.lastName}
                      <div className="text-[10px] text-muted-foreground">{fmtDate(e.hireDate)}{e.terminationDate ? ` – ${fmtDate(e.terminationDate)}` : ""}</div>
                    </TableCell>
                    {MONTH_LABELS.map((_, i) => {
                      const employed = e.months[i]?.employed;
                      return (
                        <TableCell key={i} className="px-1">
                          <Input
                            value={grid[e.employeeId]?.[i] ?? ""}
                            onChange={(ev) => update(e.employeeId, i, ev.target.value)}
                            placeholder={employed ? "est." : "—"}
                            disabled={!employed}
                            className="h-7 text-xs px-1 text-center"
                            inputMode="decimal"
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <DialogFooter>
          <span className="text-xs text-muted-foreground mr-auto">{dirty.size} cell(s) changed</span>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={save.isPending || dirty.size === 0}>
            {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save and recalculate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
